import Badge from 'components/Badge';
import Icon, { type IconName } from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import type {
	AnchorHTMLAttributesWithRef,
	ButtonHTMLAttributesWithRef,
} from 'types';
import { cn, cnm } from 'utils/classNames';

export type IconButtonSize = 'lg' | 'md' | 'sm' | 'xs';
export type IconButtonVariant =
	| 'borderless'
	| 'branded'
	| 'cta'
	| 'secondary'
	| 'transparent';

interface BaseProps {
	/** Small floating badge */
	badge?: string | number;

	/** Additional class names */
	className?: string;

	/** If the button is disabled, or without href for links */
	disabled?: boolean;

	/** Icon name */
	icon: IconName;

	/** Disable and show a loading spinner */
	isLoading?: boolean;

	/** Button size */
	size?: IconButtonSize;

	/** Text label */
	text: string;

	/** Classes for the text label, when visible */
	textClassName?: string;

	/** How the text should be displayed */
	textDisplay?: 'hidden' | 'leading' | 'trailing';

	/** Button style variant */
	variant?: IconButtonVariant;
}

type AnchorAttrs = AnchorHTMLAttributesWithRef<HTMLAnchorElement>;
type ButtonAttrs = ButtonHTMLAttributesWithRef<HTMLButtonElement>;

interface AnchorProps extends BaseProps, Omit<AnchorAttrs, 'children'> {
	type?: never;

	/** Link URL, results in an anchor instead of a button */
	href: string;
}

interface ButtonProps extends BaseProps, Omit<ButtonAttrs, 'children'> {
	href?: never;

	/** Button click handler */
	onClick: ButtonAttrs['onClick'];
}

type Props = AnchorProps | ButtonProps;

/** Icon button with a gray circle on hover. */
export default function IconButton({
	badge,
	className,
	disabled = false,
	href,
	icon,
	isLoading = false,
	onClick,
	ref,
	size = 'md',
	text,
	textClassName = '',
	textDisplay = 'hidden',
	type = 'button',
	variant = 'borderless',
	...attrs
}: Props) {
	const isInteractive = !disabled && !isLoading;
	const isTextVisible = textDisplay !== 'hidden';
	const textNode = isTextVisible ? (
		<span
			aria-hidden="true"
			className={cnm(
				textDisplay === 'leading' && 'mr-2',
				textDisplay === 'trailing' && 'ml-2',
				disabled && 'text-disabled',
				isInteractive && 'group-hover/icon-button:underline',
				textClassName,
			)}
		>
			{text}
		</span>
	) : null;

	return (
		<LinkOrButton
			{...attrs}
			ref={ref}
			aria-label={badge ? `${text} (${badge})` : text}
			className={cnm(
				'group/icon-button inline-flex items-center rounded-full align-middle',
				// Make the hit area larger for small sizes.
				(size === 'xs' || size === 'sm') &&
					isInteractive && [
						'relative after:absolute',
						// Don't expand horizontally if the text is visible.
						size === 'xs' &&
							(isTextVisible
								? 'after:inset-x-0 after:-inset-y-3'
								: 'after:-inset-3'),
						size === 'sm' &&
							(isTextVisible
								? 'after:inset-x-0 after:-inset-y-1.5'
								: 'after:-inset-1.5'),
					],
				className,
			)}
			disabled={disabled || isLoading}
			href={href}
			onClick={onClick}
			type={type}
		>
			{textDisplay === 'leading' && textNode}
			<span
				aria-hidden="true"
				className={cnm(
					'relative inline-flex items-center justify-center rounded-full',

					// Always have a border for forced colors mode.
					'border border-transparent',
					isInteractive &&
						'forced-colors:group-hover/icon-button:outline-solid',

					size === 'xs' && 'size-6',
					size === 'sm' && 'size-9',
					size === 'md' && 'size-12',
					size === 'lg' && 'size-14',

					variant === 'branded' && [
						!disabled && 'bg-brand text-inverse',
						isInteractive &&
							'group-hover/icon-button:bg-brandHover group-active/icon-button:bg-brandActive',
						disabled && 'bg-disabled text-disabled',
					],
					variant === 'cta' && [
						!disabled && 'bg-action text-inverse',
						isInteractive &&
							'group-hover/icon-button:bg-actionHover group-active/icon-button:bg-actionActive',
						disabled && 'bg-disabled text-disabled',
					],
					variant === 'secondary' && [
						'bg-base',
						!disabled && 'border-strong text-base',
						isInteractive &&
							'group-hover/icon-button:bg-baseHover group-active/icon-button:bg-baseActive',
						disabled && 'border-subtle text-disabled',
					],
					variant === 'borderless' && [
						'bg-transparent',
						!disabled && 'text-base',
						isInteractive &&
							'group-hover/icon-button:bg-inverse/20 group-active/icon-button:bg-inverse/30',
						disabled && 'text-disabled',
					],
					variant === 'transparent' && [
						'bg-positive/80',
						!disabled && 'text-inverse',
						isInteractive &&
							'group-hover/icon-button:bg-positive/90 group-active/icon-button:bg-positive',
						disabled && 'bg-disabled text-disabled',
					],
				)}
			>
				<Icon
					name={isLoading ? 'loading' : icon}
					size={size === 'xs' || size === 'sm' ? 20 : 24}
				/>
				<Badge
					text={badge}
					size={size === 'xs' || size === 'sm' ? 'sm' : 'md'}
					className={cn(
						'absolute',
						size === 'xs' && '-top-1.5 -right-1.5',
						size === 'sm' && '-top-0.5 -right-0.5',
						size === 'md' && 'top-0 right-0',
						size === 'lg' && 'top-0.5 right-0.5',
					)}
				/>
			</span>
			{textDisplay === 'trailing' && textNode}
		</LinkOrButton>
	);
}
IconButton.displayName = 'IconButton';
